import { CameraIcon } from "@heroicons/react/24/outline";
import { useTranslation } from "react-i18next";
import { IVehicle } from "../../models/Vehicle";
import openInNewTab from "../../utils/openInNewTab";
import Card from "../shared/Card/Card";
import { IPart, PartStatus } from "../../models/Part";
import useVehicleSingle from "../../data/useVehicleSingle";
import useJobsByVehicle from "../../data/useJobsByVehicle";
import { useEffect, useState } from "react";
import PartImage from "../stock/PartImage";
import Tabs from "../shared/Tabs/Tabs";
import { ButtonInput } from "../shared/Inputs/ButtonInput";
import { PlusIcon } from "@heroicons/react/24/outline";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import PriceFormat from "../../utils/priceFormat";
import { useLabelGenerator } from "../../hooks/useLabelGenerator";
import VehiclePartHistory from "./VehiclePartHistory";
import RsCoreImage from "../core/RsCoreImage";



export default function VehiclePartsCard({ vehicle: passedVehicle, vehicleId }: { vehicle?: IVehicle, vehicleId?: string }) {
  const { t } = useTranslation("vehicles");
  const { vehicle: singleVehicle } = useVehicleSingle(vehicleId);
  const { jobs, createWithPart } = useJobsByVehicle(vehicleId || passedVehicle?.id!);
  const { saveHandlers } = useDefaultCRUDHandlers(t("Job"));
  const { generatePartLabel } = useLabelGenerator();

  const [selectedPart, setSelectedPart] = useState<IPart | null>(null);

  const [vehicle, setVehicle] = useState<IVehicle | undefined>(passedVehicle);
  const [viewType, setViewType] = useState(0);
  const [availableParts, setAvailableParts] = useState<any[]>([]);

  useEffect(() => {
    if (!singleVehicle.isLoading && singleVehicle.data?.data) {
      setVehicle(singleVehicle.data?.data);
    }

    if (passedVehicle) {
      setVehicle(passedVehicle);
    }
  }, [singleVehicle.isLoading, singleVehicle.data, passedVehicle]);

  useEffect(() => {
    if (!jobs.isLoading && jobs.data && vehicle?.parts) {
      // Get all part category IDs that are currently in jobs
      const jobPartCategoryIds = new Set<string>();
      jobs.data.data.forEach(job => {
        job.jobPartCategories.forEach(jobPart => {
          if (jobPart.partCategoryId) {
            jobPartCategoryIds.add(jobPart.partCategoryId);
          }
        });
      });

      // Filter parts that are "On The Car" and not in any jobs
      const available = vehicle?.parts?.filter(part =>
        part &&
        part.partStatus === PartStatus["On The Car"] &&
        part.categoryId &&
        !jobPartCategoryIds.has(part.categoryId)
      );

      setAvailableParts(available);
    }
  }, [jobs.isLoading, jobs.data, vehicle?.parts]);


  const handleCreateJob = (part: any) => {
    // Get the current job for this vehicle (if there is one)
    const currentJobId = jobs.data?.data && jobs.data.data.length > 0 ? jobs.data.data[0].id : null;

    // console.log("Create job for part:", part);
    // console.log("Current job for vehicle:", currentJobId);

    const body = {
      partId: part.id,
      jobId: currentJobId
    }

    console.log("Create job request body:", body);

    createWithPart.mutate(body, saveHandlers);
  };

  const formatPartStatus = (status: PartStatus) => {
    const classes = "px-4 max-w-36 text-xs rounded-full h-10 flex justify-center items-center";
    let colour = "bg-gray-200 text-gray-500";

    if (status === null || status === undefined) {
      return (
        <div className={`${classes} ${colour}`}>
          <p className="">{t("N/A")}</p>
        </div>
      )
    }

    switch (status) {
      case PartStatus["On The Car"]:
        colour = "bg-orange-200 text-orange-500";
        break;
      case PartStatus.Damaged:
        colour = "bg-red-200 text-red-500";
        break;
      case PartStatus.Missing:
        colour = "bg-yellow-200 text-yellow-500";
        break;
      case PartStatus.Deleted:
        colour = "bg-gray-200 text-gray-500";
        break;
      case PartStatus.QC:
        colour = "bg-purple-200 text-purple-500";
        break;
      case PartStatus.Dismantled:
        colour = "bg-green-200 text-green-500";
        break;
      case PartStatus.Sold:
        colour = "bg-blue-200 text-blue-500";
        break;
      default:
        break;
    }

    return (
      <div className={`${classes} ${colour}`}>
        <p className="">{t(PartStatus[status])}</p>
      </div>
    )
  };

  const handlePrint = async (part: IPart | null) => {
    if (!part) return;

    part.vehicle = vehicle ?? null;

    const html = await generatePartLabel(part);
    var iframe = document?.getElementById("frame") as HTMLIFrameElement;
    var pri = iframe.contentWindow;
    pri?.document.open();
    pri?.document.write(html);
    pri?.document.close();
    pri?.focus();

    //QR code doesn't show up without a timeout
    setTimeout(async () => {
      pri?.print();
    }, 1);
  };

  if (!vehicle) return <></>;

  const viewTypes = [
    { value: 0, label: t("All Parts") },
    { value: 1, label: t("Available") },
  ];

  const renderPartsTable = (parts: any[], showCreateJobButton = false) => (
    <table className="min-w-full">
      <thead className="text-left">
        <tr>
          <th>{t("Thumbnail")}</th>
          <th>{t("Part")}</th>
          <th>{t("Tag Number")}</th>
          <th>{t("Price")}</th>
          <th>{t("Status")}</th>
          <th>{t("Actions")}</th>
        </tr>
      </thead>
      <tbody>
        {parts.filter((part) => part !== null)
          .map((part, i) => (
            <tr
              key={part.id}
              className="cursor-pointer hover:bg-gray-100 border-b"
              onClick={() => openInNewTab(`/stock/${part.id}`)}
            >
              <td className="py-2">
                <PartImage part={part} width="max-w-24" />
              </td>
              <td>{part.title}</td>
              <td>{part.tagNumber}</td>
              <td>{PriceFormat(part.price ?? part.enteredPrice ?? 0)}</td>
              <td>
                <div className="flex gap-4">
                  {formatPartStatus(part.partStatus)}
                  {part.isCore && (
                    <RsCoreImage />
                  )}
                </div>
              </td>

              <td className="py-2 flex gap-x-2" onClick={(e) => e.stopPropagation()}>
                <ButtonInput onClick={() => handlePrint(part)} label={t("Print Label")} classes="" />

                <ButtonInput onClick={() => setSelectedPart(part)} label={t("View History")} classes="" />

                {showCreateJobButton && (
                  <ButtonInput
                    isSubmit={false}
                    onClick={() => handleCreateJob(part)}
                    classes="!bg-primary-600 !text-white hover:!bg-primary-700"
                    isLoading={createWithPart.isLoading}
                  >
                    <span className="flex items-center gap-x-2 p-2 px-3">
                      <PlusIcon height={15} width={15} />
                      {t("Create Job")}
                    </span>
                  </ButtonInput>
                )}
              </td>

            </tr>
          ))}
      </tbody>
    </table>
  );

  return (
    <Card title={t("Vehicle Parts")} bodyClassName="p-4 space-y-4" collapsible>
      <VehiclePartHistory part={selectedPart} open={!!selectedPart} onClose={setSelectedPart} />
      {(!vehicle?.parts || vehicle?.parts?.length === 0) ? (
        <p className="text-gray-500">{t("No parts available for this vehicle.")}</p>
      ) : (
        <>
          <Tabs viewType={viewType} viewTypes={viewTypes} setViewType={setViewType} />

          {viewType === 0 && (
            renderPartsTable(vehicle.parts, false)
          )}

          {viewType === 1 && (
            availableParts.length > 0 ? (
              renderPartsTable(availableParts, true)
            ) : (
              <p className="text-gray-500">{t("No available parts found. All parts are either already in jobs or have been removed from the vehicle.")}</p>
            )
          )}
        </>
      )}
      <iframe id="frame" className="h-0 w-0 absolute"></iframe>
    </Card>
  )
}